import type {
  WorkspaceIntroCardProps,
  WorkspaceIntroSection,
  WorkspaceIntroStep,
} from '../../../shared/ui/common/WorkspaceIntroCard';
import type {
  WorkspaceSpotlightTourDetail,
  WorkspaceSpotlightTourStep,
} from '../../../shared/ui/common/WorkspaceSpotlightTour';

interface ValidationGuideContentArgs {
  unresolvedFamilyCount: number;
  totalIssueCount: number;
  selectedValidationCount: number;
  totalValidationCount: number;
  focusedSystemName?: string;
}

interface ValidationTourRefs {
  headerRef: WorkspaceSpotlightTourStep['targetRef'];
  railToggleRef: WorkspaceSpotlightTourStep['targetRef'];
  headerRunContextRef: WorkspaceSpotlightTourStep['targetRef'];
  headerActionsRef: WorkspaceSpotlightTourStep['targetRef'];
  headerGuideRef: WorkspaceSpotlightTourStep['targetRef'];
  headerConditionStudioRef: WorkspaceSpotlightTourStep['targetRef'];
  headerConfigureRef: WorkspaceSpotlightTourStep['targetRef'];
  headerRunRef: WorkspaceSpotlightTourStep['targetRef'];
  headerDetailToggleRef: WorkspaceSpotlightTourStep['targetRef'];
  railRef: WorkspaceSpotlightTourStep['targetRef'];
  railSearchRef: WorkspaceSpotlightTourStep['targetRef'];
  railFiltersRef: WorkspaceSpotlightTourStep['targetRef'];
  railRepairAreaRef: WorkspaceSpotlightTourStep['targetRef'];
  railPassedRef: WorkspaceSpotlightTourStep['targetRef'];
  shellRailResizeRef: WorkspaceSpotlightTourStep['targetRef'];
  shellDetailResizeRef: WorkspaceSpotlightTourStep['targetRef'];
  issueListRef: WorkspaceSpotlightTourStep['targetRef'];
  queueKeyboardHelpRef: WorkspaceSpotlightTourStep['targetRef'];
  queueSelectedCardRef: WorkspaceSpotlightTourStep['targetRef'];
  queueSelectedCardActionRef: WorkspaceSpotlightTourStep['targetRef'];
  detailRef: WorkspaceSpotlightTourStep['targetRef'];
  detailRecommendedRef: WorkspaceSpotlightTourStep['targetRef'];
  detailQuickFixRef: WorkspaceSpotlightTourStep['targetRef'];
  detailClassificationRef: WorkspaceSpotlightTourStep['targetRef'];
  detailFallbackGuidanceRef: WorkspaceSpotlightTourStep['targetRef'];
  detailAlternatePathsRef: WorkspaceSpotlightTourStep['targetRef'];
  detailImpactSummaryRef: WorkspaceSpotlightTourStep['targetRef'];
  detailMoreDetailsRef: WorkspaceSpotlightTourStep['targetRef'];
  detailAffectedItemsRef: WorkspaceSpotlightTourStep['targetRef'];
  detailTopAffectedTypesRef: WorkspaceSpotlightTourStep['targetRef'];
  detailSampleInstancesRef: WorkspaceSpotlightTourStep['targetRef'];
  detailRawIssueDetailsRef: WorkspaceSpotlightTourStep['targetRef'];
  detailCopyActionRef: WorkspaceSpotlightTourStep['targetRef'];
}

interface ValidationTourContentArgs {
  compactMode: boolean;
  inlineDetailMode: boolean;
  refs: ValidationTourRefs;
}

const section = (title: string, bullets: string[]): WorkspaceIntroSection => ({
  title,
  bullets,
});

const step = (label: string, goal: string, checks?: string[]): WorkspaceIntroStep => ({
  label,
  goal,
  checks,
});

const detail = (
  label: string,
  bullets: string[],
  tone: WorkspaceSpotlightTourDetail['tone'] = 'default'
): WorkspaceSpotlightTourDetail => ({
  label,
  bullets,
  tone,
});

const withTarget = (
  targetRef: WorkspaceSpotlightTourStep['targetRef'],
  fallbackRect: NonNullable<WorkspaceSpotlightTourStep['fallbackRect']>,
  base: Omit<WorkspaceSpotlightTourStep, 'targetRef' | 'fallbackRect'>
): WorkspaceSpotlightTourStep => ({
  ...base,
  targetRef,
  fallbackRect,
});

export const buildValidationGuideContent = ({
  unresolvedFamilyCount,
  totalIssueCount,
  selectedValidationCount,
  totalValidationCount,
  focusedSystemName,
}: ValidationGuideContentArgs): WorkspaceIntroCardProps => ({
  eyebrow: 'Workspace orientation',
  title: 'Validation shows what is still blocking export.',
  summary:
    'Use Validation after Mapping or Quick Fill to organize identified issues into a repair queue and direct each issue to the appropriate repair workspace.',
  hint:
    'Confirm the validation scope and enabled checks first, filter the queue to focus on relevant issues, review one issue family in detail, then follow the recommended repair workflow.',
  status:
    unresolvedFamilyCount === 0
      ? 'No unresolved families'
      : `${unresolvedFamilyCount} issue families open`,
  actionLabel: 'Start tour',
  chips: [
    `${selectedValidationCount}/${totalValidationCount} checks enabled`,
    `${totalIssueCount} total issues`,
    focusedSystemName ? `Scoped to ${focusedSystemName}` : 'Full model scope',
    'Queue + repair routing',
  ],
  sections: [
    section('Use this when', [
      'You need to identify what still prevents a reliable export after mapping or model cleanup changes.',
      'You need to determine where the issue should be resolved.',
    ]),
    section('Not for', [
      'Do not use Validation to plan the repair when the issue group already points to the appropriate repair workspace.',
      'Do not compare repair queues from different validation runs unless the validation scope and enabled checks are the same.',
    ]),
    section('Focus during this review', [
      'Confirm the validation scope and enabled checks before relying on the repair queue.',
      'Use search and repair-area filters to reduce clutter before opening an issue group.',
      'Follow the recommended path by default unless the detail panel provides a clear reason to choose a different action.',
    ]),
    section('Handoff', [
      'Before handoff, resolve all blocking issue groups or document why any remaining warnings are acceptable.',
      'Complete the required repair in the appropriate workspace, then run validation again to confirm the result.',
    ]),
  ],
  steps: [
    step('1. Confirm scope and checks', 'Before reviewing any issue group, confirm that the validation scope and enabled checks match your review goal.', [
      'The validation scope matches the review goal.',
      'The enabled checks are appropriate for that goal.',
    ]),
    step('2. Refresh after meaningful changes', 'Use Run validation as a checkpoint after meaningful model, mapping, or repair changes, rather than after every individual edit.', [
      'The next validation run should confirm a meaningful change or answer a new review question.',
    ]),
    step('3. Filter the queue before review', 'Use search, severity filters, and repair-area grouping to narrow the queue before opening an issue group.', [
      'Higher-priority issues are easy to identify, and unrelated items are filtered out.',
    ]),
    step('4. Review one issue group in detail', 'Review the issue summary, primary action, alternative actions, impact, and available copy options. Use Run validation, Configure, or Condition Studio when the issue requires one of those workflows.', [
      'You understand why the issue requires attention.',
      'You know which workflow or action should be used next.',
    ]),
    step('5. Repair, rerun, and confirm', 'Complete the recommended repair, then run validation again to confirm that the issue has improved or been resolved.', [
      'The blocking issue group is reduced or resolved, or any remaining issue has been intentionally deferred with a documented reason.',
    ]),
  ],
});

export const buildValidationTourSteps = ({
  compactMode,
  inlineDetailMode,
  refs,
}: ValidationTourContentArgs): WorkspaceSpotlightTourStep[] => {
  const detailPanelWidth = Math.min(520, Math.max(window.innerWidth * 0.34, 440));
  const detailPanelLeft = Math.max(window.innerWidth - detailPanelWidth - 24, 24);
  const detailSectionWidth = Math.max(detailPanelWidth - 48, 376);
  const headerRect = {
    top: 68,
    left: 24,
    width: Math.max(window.innerWidth - 48, 360),
    height: 112,
  };
  const railRect = {
    top: 192,
    left: 24,
    width: 280,
    height: Math.max(window.innerHeight - 240, 260),
  };
  const queueRect = {
    top: 192,
    left: 328,
    width: Math.max(window.innerWidth * 0.34, 320),
    height: Math.max(window.innerHeight - 240, 260),
  };
  const detailRect = {
    top: 192,
    left: detailPanelLeft,
    width: detailPanelWidth,
    height: Math.max(window.innerHeight - 240, 260),
  };
  const railToggleRelatedTarget = {
    label: 'Open triage rail button',
    targetRef: refs.railToggleRef,
  };
  const railAccessProps: Pick<WorkspaceSpotlightTourStep, 'relatedTargets' | 'relatedTargetsLabel'> =
    compactMode
      ? {
          relatedTargets: [railToggleRelatedTarget],
          relatedTargetsLabel: 'Access',
        }
      : {};
  const drawerDetailAccessStep: WorkspaceSpotlightTourStep[] = inlineDetailMode
    ? []
    : [
        withTarget(refs.headerDetailToggleRef, { top: 122, left: Math.max(window.innerWidth - 348, 24), width: 36, height: 36 }, {
          id: 'header-detail-access',
          title: 'Toggle Detail Panel',
          focusLabel: 'Show or hide selected issue details',
          intro: 'Use this control to open or close the detail panel for the selected issue group.',
          details: [
            detail('How to use it', [
              'Use Toggle Detail Panel only to show or hide the panel. Opening it does not change or resolve the selected issue.',
              'When the panel is open, use its sections to review issue details and available repair options.',
            ]),
          ],
          nextAction: 'Open the detail panel when you need more information about the selected issue group, then review the relevant section before choosing a repair action.',
        }),
      ];
  const shellResizeSteps: WorkspaceSpotlightTourStep[] = compactMode
    ? []
    : [
        withTarget(
          refs.shellRailResizeRef,
          { top: railRect.top, left: 248, width: 12, height: railRect.height },
          {
            id: 'shell-rail-resizer',
            title: 'Resize the Triage filters panel',
            focusLabel: 'Triage filters width handle',
            intro: 'Use the resize handle to make the Triage filters panel wider or narrower.',
            details: [
              detail('How to use it', [
                'Drag the handle when filter labels, repair category groups, or long search terms need more space.',
                'When using keyboard navigation, use the arrow keys to adjust the panel width more precisely.',
              ]),
            ],
            nextAction: 'Resize the panel as needed, then continue reviewing the selected issue group.',
          }
        ),
        ...(inlineDetailMode
          ? [
              withTarget(
                refs.shellDetailResizeRef,
                { top: detailRect.top, left: detailRect.left - 12, width: 24, height: detailRect.height },
                {
                  id: 'shell-detail-resizer',
                  title: 'Resize the detail panel',
                  focusLabel: 'Detail panel width handle',
                  intro: 'The detail resize handle changes how much room the selected family evidence gets while keeping the queue selection unchanged.',
                  details: [
            detail('How to use it', [
                      'Widen the detail panel when evidence, examples, or raw issue disclosures need more readable space.',
                      'Narrow it again when the queue needs more room for comparison.',
                    ]),
                  ],
                  nextAction: 'Resize the detail panel for readability; do not treat it as a change to repair scope.',
                }
              ),
            ]
          : []),
      ];

  return [
    withTarget(refs.headerRef, headerRect, {
      id: 'header',
      title: 'Validation workspace',
      focusLabel: 'Validation workspace tab',
      intro: 'The Validation workspace contains the issue queue and repair tools for the current validation results.',
      details: [
        detail('How to use it', [
          'Confirm that the Validation workspace is active before reviewing the issue queue.',
          'Open the Tour or Page Guide when you need workflow guidance without changing validation data.',
          'Use the header controls to run validation, configure checks, or open the appropriate repair workflow.',
        ]),
        detail('Why it matters', [
          'The issue queue reflects the current validation scope and results, so confirm the workspace and validation context before reviewing issues.',
        ], 'accent'),
      ],
      nextAction: 'Confirm that Validation is active, then review the current validation scope and results before opening an issue group.',
      chromeTargetId: 'validation-workspace-tab',
    }),
    withTarget(refs.railRef, railRect, {
      id: 'rail',
      title: 'Triage rail',
      focusLabel: 'Triage rail filters',
      intro: 'Use the rail to narrow the issue queue before selecting an issue group.',
      ...railAccessProps,
      details: [
        detail('How to use it', [
          'Search for a known issue, value, or identifier.',
          'Filter by urgency or repair area when the issue queue is too broad.',
          'Use the filters to identify which issue group should be reviewed next.',
        ]),
      ],
      nextAction: 'Narrow the issue queue, then open the issue group you want to review.',
    }),
    withTarget(refs.issueListRef, queueRect, {
      id: 'queue',
      title: 'Issue group queue',
      focusLabel: 'Repair queue',
      intro: 'The queue organizes validation results into issue groups that can be reviewed and repaired.',
      details: [
        detail('What to check', [
          'Each issue group shows its title, severity, summary, and key details.',
          'Use the help icon in the repair queue header to review available keyboard shortcuts.',
          'Review one selected issue group at a time instead of moving between individual validation findings.',
        ]),
        detail('Why it matters', [
          'Grouping related validation findings makes the repair workflow easier to review and manage.',
        ], 'accent'),
      ],
      nextAction: 'Open the Fix Now issue group first, then review the recommended action in the detail panel before continuing with lower-priority issue groups.',
    }),
    withTarget(refs.detailRef, detailRect, {
      id: 'detail',
      title: 'Repair detail panel',
      focusLabel: 'Selected issue detail panel',
      intro: 'The detail panel shows the recommended repair workflow for the selected issue group and the information needed to understand it.',
      details: [
        detail('How to use it', [
          'Review the recommended action, why it is recommended, and how many elements or references are affected.',
          'Use this panel to determine whether the issue should be resolved in Mapping, Quick Fill, Condition Studio, or another repair workflow.',
        ]),
      ],
      nextAction: 'Review the recommended repair workflow, then continue in the appropriate workspace.',
    }),
    withTarget(refs.headerRunContextRef, { top: 102, left: 36, width: 520, height: 52 }, {
      id: 'header-run-context',
      title: 'Review the run context first',
      focusLabel: 'Validation run context',
      intro: 'The run context shows the model, validation scope, enabled checks, and current readiness status for the latest validation run.',
      details: [
        detail('What to confirm', [
          'First line: Revit document, last validation run, and system scope when applicable.',
          'Second line: number of enabled checks, embedded mode when applicable, and the readiness status.',
          'Confirm this information before comparing the current issue queue with an earlier validation run.',
        ]),
        detail('Why it matters', [
          'The issue queue is meaningful only when it reflects the model, scope, and checks you intend to review.',
        ], 'accent'),
      ],
      nextAction: 'Confirm the run context before comparing results or sharing the validation status with others.',
    }),
    withTarget(refs.headerActionsRef, { top: 120, left: Math.max(window.innerWidth - 600, 24), width: 576, height: 96 }, {
      id: 'header-actions',
      title: 'Header actions',
      focusLabel: 'Validation action bar',
      intro: 'Use the action bar to run validation, open Configure Validation Checks, open Condition Studio (available soon), or Toggle Detail Panel.',
      details: [
        detail('How to use it', [
          'Use these controls to update validation results, adjust validation settings or rules, or review the current validation details and available repair options.',
          'Confirm the run context first so the selected action applies to the intended validation scope.',
        ]),
      ],
      nextAction: 'Choose the header action that matches what you need to do next.',
    }),
    ...drawerDetailAccessStep,
    withTarget(refs.headerGuideRef, { top: 122, left: Math.max(window.innerWidth - 300, 24), width: 36, height: 36 }, {
      id: 'header-page-guide',
      title: 'Page guide',
      focusLabel: 'Page guide and tour access',
      intro: 'Open the Page Guide to review the Validation workflow or start the page tour.',
      details: [
        detail('How to use it', [
          'Open the guide when you are new to Validation or need a reminder before using its tools and repair actions.',
          'The guide provides workflow information only; it does not run validation or change any data.',
        ]),
      ],
      nextAction: 'Use the Page Guide when you need workflow guidance, then return to the issue queue and continue your review.',
      chromeTargetId: 'validation-guide',
    }),
    withTarget(refs.headerConditionStudioRef, { top: 122, left: Math.max(window.innerWidth - 252, 24), width: 36, height: 36 }, {
      id: 'header-condition-studio',
      title: 'Condition Studio',
      focusLabel: 'Validation rule editor',
      intro: 'Use Condition Studio when the selected issue group is caused by validation rule logic rather than a missing or incorrect data value.',
      details: [
        detail('How to use it', [
          'Open Condition Studio when a rule condition, expression, or classification rule needs to be reviewed or changed.',
          'Stay in Validation when you only need to review issues or rerun validation.',
        ]),
      ],
      nextAction: 'Open Condition Studio only when the selected issue group requires changes to validation rule logic.',
    }),
    withTarget(refs.headerConfigureRef, { top: 122, left: Math.max(window.innerWidth - 204, 24), width: 36, height: 36 }, {
      id: 'header-configure-checks',
      title: 'Configure validation checks',
      focusLabel: 'Validation check configuration',
      intro: 'Use Configure Validation Checks to choose which validation checks are included in the next validation run.',
      details: [
        detail('How to use it', [
          'Open Configure Validation Checks when the enabled checks do not match the current review goal or when a specific validation check should be included or excluded.',
          'After changing the configuration, rerun validation so the issue queue reflects the updated set of checks.',
        ]),
      ],
      nextAction: 'Adjust the enabled checks as needed, then rerun validation to update the issue queue.',
    }),
    withTarget(refs.headerRunRef, { top: 122, left: Math.max(window.innerWidth - 156, 24), width: 132, height: 36 }, {
      id: 'header-run-validation',
      title: 'Run validation',
      focusLabel: 'Updated validation results',
      intro: 'Run Validation to refresh the issue queue after changes to Mapping, Quick Fill, validation rules, or the Revit model.',
      details: [
        detail('How to use it', [
          'Rerun validation after completing a related set of repairs or changing the validation configuration, then review the updated issue-group count and severity levels.',
          'Avoid rerunning validation until there is a meaningful change to check.',
        ]),
      ],
      nextAction: 'Rerun validation when updated results are needed, then review the filters and issue queue to confirm that the expected issues have been resolved or reduced.',
    }),
    withTarget(refs.railSearchRef, { top: 242, left: 32, width: 252, height: 120 }, {
      id: 'rail-search',
      title: 'Search',
      focusLabel: 'Issue group search field',
      intro: 'Use Search to quickly find an issue group when you already know a field, name, parameter, identifier, or error message.',
      ...railAccessProps,
      details: [
        detail('How to use it', [
          'Search by issue group name, parameter, identifier, or known error text.',
          'Use Search before adding filters when a specific term can narrow the queue effectively.',
        ]),
      ],
      nextAction: 'Use Search first when a known term can lead you to the relevant issue group.',
    }),
    withTarget(refs.railRepairAreaRef, { top: 526, left: 32, width: 252, height: 150 }, {
      id: 'rail-repair-area',
      title: 'Repair category',
      focusLabel: 'Repair category grouping',
      intro: 'Use Repair category to group issue groups by the likely cause of the error, such as missing or invalid PCF export fields, parameter mapping or rule alignment problems, or inconsistent connector geometry.',
      ...railAccessProps,
      details: [
        detail('How to use it', [
          'Group issues by Mapping, Export data, Connectors, Properties, or Components.',
          'Use this grouping when you want to review and resolve issues with similar causes together.',
        ]),
        detail('Why it matters', [
          'Grouping issues by repair category makes it easier to plan and complete related repairs in one session.',
        ], 'accent'),
      ],
      nextAction: 'Choose the repair category, then review the issue groups within that category.',
    }),
    withTarget(refs.detailRecommendedRef, { top: 244, left: detailPanelLeft + 16, width: detailSectionWidth, height: 184 }, {
      id: 'detail-recommended-action',
      title: 'Recommended action',
      focusLabel: 'Recommended next step card',
      intro: 'Recommended action shows the primary action PipeEX recommends for the selected issue group.',
      details: [
        detail('What to check', [
          'Review the primary action and the recommended repair workflow.',
          'Follow the recommendation unless the detail panel provides a clear reason to choose a different action.',
        ]),
        detail('Why it matters', [
          'The recommendation helps you move quickly from identifying the issue to the appropriate repair workflow.',
        ], 'accent'),
      ],
      nextAction: 'Follow the recommended action unless the issue details indicate that another repair workflow is more appropriate.',
    }),
    withTarget(refs.detailQuickFixRef, { top: 376, left: detailPanelLeft + 16, width: detailSectionWidth, height: 144 }, {
      id: 'detail-quick-fix-options',
      title: 'Quick fix options',
      focusLabel: 'Available repair actions',
      intro: 'Quick fix options show direct fixes and repair-workflow actions available for the selected issue group.',
      details: [
        detail('How to use it', [
          'Use a direct fix only when it is available for the selected issue group and applies to the intended elements or values.',
          'Use a repair-workflow action when the issue requires additional review before changes are applied.',
        ]),
        detail('Why it matters', [
          'These options provide the available ways to resolve the selected issue without leaving Validation unnecessarily.',
        ], 'accent'),
      ],
      nextAction: 'Use a Quick fix option when it matches the selected issue group. Otherwise, review the issue details and impact before choosing another repair action.',
    }),
    withTarget(refs.detailImpactSummaryRef, { top: 704, left: detailPanelLeft + 16, width: detailSectionWidth, height: 132 }, {
      id: 'detail-impact-summary',
      title: 'Review impact before choosing a repair',
      focusLabel: 'Affected elements summary',
      intro: 'The impact summary shows how many elements or references are affected by the selected issue group.',
      details: [
        detail('What to check', [
          'Review the affected counts and details to determine whether the issue requires a targeted repair or broader cleanup.',
          'Use the impact summary to decide whether to continue with detail repair or move to the appropriate repair workspace.',
        ]),
      ],
      nextAction: 'Review the impact before continuing so the selected repair approach matches the number and type of affected elements.',
    }),
    withTarget(refs.railFiltersRef, { top: 360, left: 32, width: 252, height: 140 }, {
      id: 'rail-filters',
      title: 'Quick filters',
      focusLabel: 'Severity and focus filters',
      intro: 'Use severity or focus filters individually or together when the issue queue is still too broad after searching.',
      ...railAccessProps,
      details: [
        detail('How to use it', [
          'Narrow the queue by severity, blockers, or required checks to focus on the most relevant issue groups.',
          'Combine filters with Search instead of scrolling through a long issue list.',
        ]),
      ],
      nextAction: 'Apply only the filters needed to focus on the issue groups you want to review next.',
    }),
    withTarget(refs.railPassedRef, { top: Math.max(window.innerHeight - 220, 420), left: 32, width: 252, height: 120 }, {
      id: 'rail-passed',
      title: 'Passed',
      focusLabel: 'Passed-check review',
      intro: 'Expand Passed Checks to review which validation checks have been completed successfully.',
      ...railAccessProps,
      details: [
        detail('How to use it', [
          'Switch to the passed-only view when you want to review successful checks after completing repairs.',
          'Return to unresolved issue groups when additional review or repair is still required.',
        ]),
        detail('Avoid', [
          'Do not hide unresolved issues simply to shorten the queue.',
        ], 'caution'),
      ],
      nextAction: 'Review passed checks after confirming that the unresolved queue shows the expected validation results.',
    }),
    ...shellResizeSteps,
    withTarget(refs.queueKeyboardHelpRef, { top: 210, left: Math.max(window.innerWidth * 0.58, 520), width: 40, height: 40 }, {
      id: 'queue-keyboard-help',
      title: 'Queue keyboard help',
      focusLabel: 'Repair queue keyboard controls',
      intro: 'The queue help icon shows the keyboard shortcuts available for navigating between issue groups.',
      details: [
        detail('How to use it', [
          'Use the shortcuts to move through a long issue queue while keeping the selected issue details visible in the detail panel.',
          'Keyboard navigation lets you review issue groups without changing filters or leaving the Validation workspace.',
        ]),
      ],
      nextAction: 'Open the keyboard help when you want to navigate the issue queue using the keyboard, then review one selected issue group at a time.',
    }),
    withTarget(refs.queueSelectedCardRef, { top: 220, left: 340, width: Math.max(window.innerWidth * 0.32, 300), height: 120 }, {
      id: 'queue-selected-card',
      title: 'Selected issue group card',
      focusLabel: 'Active issue group card',
      intro: 'The highlighted card summarizes the selected issue group, including its severity, counts, and key details.',
      details: [
        detail('What to check', [
          'Review one issue group at a time so the detail panel reflects the currently selected card.',
          'Confirm that the selected card matches the issue you intend to review or repair.',
        ]),
      ],
      nextAction: 'Confirm the selected issue group, then review its details and recommended actions in the detail panel.',
    }),
    withTarget(refs.queueSelectedCardActionRef, { top: 300, left: 360, width: 220, height: 44 }, {
      id: 'queue-selected-card-action',
      title: 'Primary action on the card',
      focusLabel: 'Card-level primary action',
      intro: 'The primary action on the card opens the recommended repair workflow when one is available.',
      details: [
        detail('How to use it', [
          'Use the card action when Validation has already identified a clear next workflow.',
          'Open the full detail panel when you need more information about the issue, its impact, or alternative repair actions.',
        ]),
      ],
      nextAction: 'Use the card action to continue directly to the recommended repair workflow, or open the detail panel when you need more information before proceeding.',
    }),
    withTarget(refs.detailClassificationRef, { top: 420, left: detailPanelLeft + 16, width: detailSectionWidth, height: 120 }, {
      id: 'detail-classification',
      title: 'Issue details',
      focusLabel: 'Classification and severity',
      intro: 'Classification shows how the selected issue group is categorized and how severe the validation problem is.',
      details: [
        detail('What to check', [
          'Review the severity and category to confirm that the issue group matches the problem you expect to resolve.',
        ]),
      ],
      nextAction: 'Review the classification when you need more context before choosing or confirming the recommended repair action.',
    }),
    withTarget(refs.detailFallbackGuidanceRef, { top: 508, left: detailPanelLeft + 16, width: detailSectionWidth, height: 112 }, {
      id: 'detail-fallback-guidance',
      title: 'Fallback guidance',
      focusLabel: 'Alternative repair guidance',
      intro: 'Fallback guidance explains when the recommended repair workflow may not be appropriate and when additional inspection or scope review is needed.',
      details: [
        detail('How to use it', [
          'Review the guidance when the recommended action does not match the details of the selected issue group.',
          'Use it only when the issue details indicate that another repair approach may be more appropriate.',
        ]),
      ],
      nextAction: 'Choose an alternative repair approach only when that approach matches the selected issue group.',
    }),
    withTarget(refs.detailAlternatePathsRef, { top: 540, left: detailPanelLeft + 16, width: detailSectionWidth, height: 140 }, {
      id: 'detail-alternate-paths',
      title: 'Other options',
      focusLabel: 'Alternative repair options',
      intro: 'When more than one repair workflow is available, this section shows the relevant alternatives.',
      details: [
        detail('How to use it', [
          'Compare alternative options when the recommended workflow is blocked by permissions, missing data, or project requirements.',
          'Choose an appropriate alternative.',
        ]),
        detail('Why it matters', [
          'The correct repair workflow may differ depending on the issue details and available actions.',
        ], 'accent'),
      ],
      nextAction: 'Choose an alternative repair path only when the issue details support a different workflow.',
    }),
    withTarget(refs.detailMoreDetailsRef, { top: 720, left: detailPanelLeft + 16, width: detailSectionWidth, height: 164 }, {
      id: 'detail-more-details',
      title: 'More details and raw issues',
      focusLabel: 'Additional issue details',
      intro: 'More details provides information about affected items, types, instances, examples, and raw validation messages for deeper review.',
      details: [
        detail('How to use it', [
          'Expand these sections when you need additional evidence, examples, or exact validation details before choosing a repair action or sharing the issue with the team.',
          'Keep them collapsed when the recommended repair action and impact summary already provide enough information.',
        ]),
      ],
      nextAction: 'Open the additional details only when you need more information to understand, repair, or document the selected issue group.',
    }),
    withTarget(refs.detailAffectedItemsRef, { top: 760, left: detailPanelLeft + 16, width: detailSectionWidth, height: 72 }, {
      id: 'detail-affected-items',
      title: 'Affected fields and items',
      focusLabel: 'Affected items',
      intro: 'This section lists the exact parameters, components, or item names affected by the selected issue group.',
      details: [
        detail('How to use it', [
          'Expand the section when you need exact field names or affected items before choosing a repair action.',
          'Use the preview when the affected items are already clear and you only need a quick confirmation.',
        ]),
      ],
      nextAction: 'Confirm the affected fields and items before opening a repair workflow that can change multiple elements.',
    }),
    withTarget(refs.detailTopAffectedTypesRef, { top: 824, left: detailPanelLeft + 16, width: detailSectionWidth, height: 72 }, {
      id: 'detail-top-affected-types',
      title: 'Top affected types',
      focusLabel: 'Affected types',
      intro: 'This section groups affected items by component type, making broader patterns easier to identify.',
      details: [
        detail('How to use it', [
          'Expand the section to see whether the selected issue group affects mainly one component type or several.',
          'Use this information to determine whether the repair should target individual elements, a specific type, or a broader set of components.',
        ]),
      ],
      nextAction: 'Review the affected types when the issue extends beyond a single field or element.',
    }),
    withTarget(refs.detailSampleInstancesRef, { top: 888, left: detailPanelLeft + 16, width: detailSectionWidth, height: 72 }, {
      id: 'detail-sample-instances',
      title: 'Sample affected instances',
      focusLabel: 'Instance examples',
      intro: 'This section provides example affected instances and their Revit element IDs for model review.',
      details: [
        detail('How to use it', [
          'Expand the section when you need specific examples to verify the issue or repair in Revit.',
          'Use the element IDs to locate individual instances without reviewing the entire affected set.',
        ]),
      ],
      nextAction: 'Review the sample instances when you need specific Revit elements for inspection or verification.',
    }),
    withTarget(refs.detailRawIssueDetailsRef, { top: 952, left: detailPanelLeft + 16, width: detailSectionWidth, height: 72 }, {
      id: 'detail-raw-issue-details',
      title: 'Raw issue details',
      focusLabel: 'Raw validation details',
      intro: 'This section provides the original validation messages for deeper technical review without cluttering the main issue summary.',
      details: [
        detail('How to use it', [
          'Expand the raw issue details section when the summarized issue information is not sufficient for debugging, rule review, or documentation.',
          'Keep the section collapsed during normal review when the issue summary and recommended repair action provide enough information.',
        ]),
      ],
      nextAction: 'Review the raw issue details when you need additional technical information about the selected issue group.',
    }),
    withTarget(refs.detailCopyActionRef, { top: 640, left: detailPanelLeft + 16, width: 200, height: 44 }, {
      id: 'detail-copy-issue',
      title: 'Copy issue summary',
      focusLabel: 'Copy issue summary action',
      intro: 'Copy a summary of the selected issue group, including its title, severity, affected targets, and examples.',
      details: [
        detail('How to use it', [
          'Use this action when you need to share the issue details in chat, documentation, or a support ticket.',
          'Before sharing, confirm that the copied summary matches the selected issue group.',
        ]),
      ],
      nextAction: 'Copy the summary after confirming the selected issue group, then paste it where the issue needs to be shared or documented.',
    }),
  ];
};
